/* eslint-disable */
import { NeuAvatar, NeuUser } from '@neutron/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IUserInfo } from '../../shared/interfaces/UserInfo';
import { APP_URL } from '../../shared/config/environment';
import { RESPONSE_SUCESS } from '../../shared/constants/response-constants';
import { AccountSummaryRequest, APIResponseOfUpdateResponseDTO } from '../../services/generated';

import useApi from '../../hooks/use-api';

import AccountSearchBar from '../search-bar/AccountSearchBar';
import { Icon, IconType } from '../icon/Icon';
import { Button } from '../button/Button';
import { Image } from '../image/Image';
import { NavTab, NavTabContainer } from '../navtab/NavTab';

import './Header.scss';
import { useWorkqueueContext } from '../../pages/workqueue/WorkqueueContext';
import { WorkqueueRefMapping } from '../../shared/constants/ui-constants';

interface HeaderProps {
  userInfo: IUserInfo;
  updateSearchStatus: any;
  isSearchActive: boolean;
}

function Header({ userInfo, updateSearchStatus, isSearchActive }: HeaderProps) {
  const { workqueueValue, setWorkqueueValue } = useWorkqueueContext();
  const location = useLocation();
  const navigate = useNavigate();
  const { account: accountClient } = useApi();

  const globalRef = globalThis as any;

  /* ----------------------------------
   * Environment label & icon
   * ---------------------------------- */
  const { icon, label } = useMemo(() => {
    const env = process.env.REACT_APP_ENVIRONMENT;
    if (env === 'qa') return { icon: 'settings', label: 'QA' };
    if (env === 'development') return { icon: 'settings', label: 'DEV' };
    return { icon: null, label: '' };
  }, []);

  /* ----------------------------------
   * Handlers
   * ---------------------------------- */
  const handleAdvanceSearch = useCallback(() => {
    updateSearchStatus(true);
  }, []);

  const updateAppAccountLock = useCallback(
    async (payload: AccountSummaryRequest) => {
      const response: APIResponseOfUpdateResponseDTO =
        await accountClient.updateAppAccountLock(payload);

      if (response.statusCode === RESPONSE_SUCESS && response.result) {
        globalRef.isAccountLockedForOtherUser = false;
        window.location.href = APP_URL;
      }
    },
    [accountClient, globalRef]
  );

  const goToHome = useCallback(() => {
    const { lastVisitedAccountNumber, lastVisitedAccountCOID } = globalRef;

    if (lastVisitedAccountNumber && lastVisitedAccountCOID) {
      updateAppAccountLock({
        accountNumber: lastVisitedAccountNumber,
        coid: Number(lastVisitedAccountCOID),
      } as AccountSummaryRequest);

      globalRef.lastVisitedAccountNumber = undefined;
      globalRef.lastVisitedAccountCOID = undefined;
    } else {
      window.location.href = APP_URL;
    }
  }, [updateAppAccountLock, globalRef]);

  const handleNavigation = useCallback(
    (url: string) => {
      globalRef.resetWorkQueueValue = true;
      globalRef.selectedWorkqueue = WorkqueueRefMapping.FollowUpWorkQueue;

      updateSearchStatus(false);

      setWorkqueueValue((prev) =>
        prev
          ? {
              ...prev,
              isListview: false,
              isHighPriorityWorkqueue: false,
              fromDate: undefined,
              toDate: undefined,
            }
          : prev
      );

      navigate(url);
    },
    [navigate, globalRef, updateSearchStatus, setWorkqueueValue]
  );

  /* ----------------------------------
   * Render
   * ---------------------------------- */
  return (
    <div className="app-header">
      <div className="app-header-inner container-fluid">
        {/* LEFT */}
        <div className="header-start">
          <a onClick={goToHome}>
            <div className="d-flex align-items-center app-logo">
              <Image
                width={118}
                alt="TEC Tool"
                src="/assets/images/Parallon-Logo.png"
              />
            </div>
          </a>

          <a onClick={goToHome}>
            <div className="text-bold neu-text-plain-0 neu-titleText text-center">
              TEC Tool
            </div>
          </a>

          <NavTabContainer className="tab-list mx-4">
            <NavTab
              active={location.pathname.includes('/workqueue')}
              onClick={() => handleNavigation('/workqueue')}
            >
              Work Queues
            </NavTab>

            <NavTab
              active={location.pathname.includes('/pre-registration')}
              onClick={() => handleNavigation('/pre-registration')}
            >
              Pre Registration
            </NavTab>

            <NavTab
              active={location.pathname.includes('/maintenance')}
              onClick={() =>
                handleNavigation('/maintenance')
              }
            >
              Maintenance
            </NavTab>

            {icon && (
              <div className="text-bold qa-icon-div">
                <Icon value={icon as IconType} />
                <span className="ms-2">{label}</span>
              </div>
            )}
          </NavTabContainer>
        </div>

        {/* RIGHT */}
        <div className="header-end">
          <div className="header-search-input-wrap">
            <AccountSearchBar
              label="Search Account"
              reset="true"
              path={location.pathname}
            />

            <Button
              className="adv-search-btn"
              color="blue"
              onClick={handleAdvanceSearch}
            >
              <Icon value="zoom_in" />
            </Button>
          </div>

          <NeuUser
            avatar
            firstName={userInfo?.firstName}
            lastName={userInfo?.lastName}
            className="d-md-flex justify-content-end"
            shortName={`Welcome, ${userInfo?.firstName ?? ''} ${
              userInfo?.lastName ?? ''
            }`}
          />
          <NeuAvatar initials={`${userInfo?.firstName?.charAt(0) ?? ''}${userInfo?.lastName?.charAt(0) ?? ''}`} color="gray" icon="person" />
        </div>
      </div>
    </div>
  );
}

export default Header;
